import { memo, useState } from 'react';
import { runOnJS, useAnimatedReaction } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { useFullScreenPagerTransition } from '@/feature/circular-pager/FullScreenPagerTransitionProvider';
import { FantasyPickbar, PICK_BAR_BOTTOM_DISTANCE } from '@/feature/lobby/components/FantasyPickbar';

import { useSinglePlayerProfile } from '../SinglePlayerProfileProvider';
import { PickbarSheetSync } from './PickbarSheetSync';

/**
 * Pickbar for the event details sheet
 * Appears after the events sheet's mount animation completes, then translates in sync with sheet movement
 * Hides when the player profile modal opens and reappears when it closes
 */
export const EventDetailsPickBar = memo(() => {
    const insets = useSafeAreaInsets();
    const hasPicks = useBetslipStore(state => state.betslip.length > 0);

    const { isSheetMountCompleted, animatedIndex } = useFullScreenPagerTransition();
    const { isPlayerProfileModalOpen } = useSinglePlayerProfile();

    // this is used to sync pickbar with the event sheet visibility
    const [isEventSheetOpen, setIsEventSheetOpen] = useState(false);

    // ready means: mount animation finished and profile modal is closed
    const [isPickbarReady, setIsPickbarReady] = useState(false);

    useAnimatedReaction(
        () => isSheetMountCompleted.value && !isPlayerProfileModalOpen.value,
        value => {
            runOnJS(setIsPickbarReady)(value);
        }
    );

    const shouldShowPickbarOnEventsSheet = isPickbarReady && hasPicks && isEventSheetOpen;

    return (
        <PickbarSheetSync animatedYIndex={animatedIndex} onSheetOpenChange={setIsEventSheetOpen}>
            <FantasyPickbar
                showContent={shouldShowPickbarOnEventsSheet}
                visiblePosition={insets.bottom + PICK_BAR_BOTTOM_DISTANCE}
            />
        </PickbarSheetSync>
    );
});
